import { sizeLabel, variantLabel, type StoreCode } from '@familyhub/shared';
import type {
  CategoryOrderRecord,
  GenericProductRecord,
  ListItemRecord,
  PriceRecord,
  ProductVariantRecord,
  StoreRecord,
} from '../db';
import { imagesByGeneric } from './images';

/**
 * Grouping and aisle order, computed locally (ARCHITECTURE §6.1, §6.5).
 *
 * This exists on the client because the list has to be right **in a shop with
 * no signal**, which is where aisle order matters most. It is a pure function
 * over the local mirror so it can be tested without a browser.
 *
 * The rules it implements, in the order they apply:
 *
 *   - One list, grouped by store. The **Any** group (`store: null`) always
 *     sorts first — it is not a fifth list, it is the first group.
 *   - Within a group: **category position → category name → product name**,
 *     with uncategorized last. Never first: better to meet a small unknown pile
 *     at the end of a shop than to begin with it.
 *   - A store whose adapter is down keeps its group, flagged, with items and no
 *     prices — visible rather than vanished.
 */

export interface DisplayItem {
  id: string;
  name: string;
  category: string | null;
  /**
   * The grey line: which one of this generic was meant — "Oatly Barista", not
   * the shop's "Oatly Barista Haverdrink Houdbaar 1L".
   *
   * Null is common and correct. An any-store item has no variant at all, and a
   * freshly selected one has a *provisional* generic named after the shop's own
   * title — so until the classifier runs, the variant name and the line above
   * it are the same string, and `variantLabel` returns null rather than
   * printing it twice.
   */
  detail: string | null;
  sizeLabel: string | null;
  /** Same-origin path for the product photograph, or null to draw the lettered tile. */
  imagePath: string | null;
  quantity: number;
  note: string | null;
  suggestionReason: string | null;
  /**
   * Whether this is an organic (*biologisch*) product — the chosen variant's
   * flag when there is one, else the generic's (a "biologische …" any-store
   * item or manual entry). Server-derived, so the tile only reads it.
   */
  organic: boolean;
  genericProductId: string;
  variantId: string | null;
  priceCents: number | null;
  priceFetchedAt: string | null;
  stale: boolean;
}

export interface DisplayGroup {
  store: StoreCode | null;
  storeId: string | null;
  label: string;
  /** False for a store whose live search is unavailable (ADAPTERS §5.3). */
  adapterOk: boolean;
  healthNote: string | null;
  items: DisplayItem[];
}

export interface GroupingInput {
  items: ListItemRecord[];
  generics: GenericProductRecord[];
  variants: ProductVariantRecord[];
  stores: StoreRecord[];
  order: CategoryOrderRecord[];
  prices: PriceRecord[];
  /** ADAPTERS §5.2 — the shorter window, since these are prices acted on in a shop. */
  staleAfterHours: number;
  /** Injectable so the test does not depend on the wall clock. */
  now?: number;
}

/** The shared formatter, not a second copy — see `sizeLabel` for why. */
const sizeLabelOf = (v: ProductVariantRecord): string | null =>
  sizeLabel(v.size_value, v.size_unit);

/**
 * Uncategorized sorts last, so it gets a position beyond every real one rather
 * than the 0 that would put it first.
 */
const UNPOSITIONED = Number.MAX_SAFE_INTEGER;

export function groupList(input: GroupingInput, filter: StoreCode | null = null): DisplayGroup[] {
  const now = input.now ?? Date.now();
  const genericById = new Map(input.generics.map((g) => [g.id, g]));
  const variantById = new Map(input.variants.map((v) => [v.id, v]));
  const storeById = new Map(input.stores.map((s) => [s.id, s]));
  const priceByVariant = new Map(input.prices.map((p) => [p.variant_id, p]));
  const genericImages = imagesByGeneric(input.variants);

  const positionOf = new Map(input.order.map((o) => [`${o.store_id}|${o.category}`, o.position]));

  const live = input.items.filter((i) => i.deleted_at === null);

  const groups = new Map<string | null, DisplayGroup>();

  // Every store gets a group even when it has no items yet? No — an empty
  // group is noise. Groups are created by the items in them, and the Any group
  // is added below only when something is in it.
  for (const item of live) {
    const store = item.store_id ? (storeById.get(item.store_id) ?? null) : null;

    // The ?store= filter returns that group **plus Any** — same shape, no
    // second list (API §5).
    if (filter && store?.code !== filter && item.store_id !== null) continue;

    const key = item.store_id;
    let group = groups.get(key);
    if (!group) {
      group = {
        store: store?.code ?? null,
        storeId: item.store_id,
        label: store?.name ?? 'Overal',
        adapterOk: store ? store.health === 'ok' || store.health === 'degraded' : true,
        healthNote: store?.health_note ?? null,
        items: [],
      };
      groups.set(key, group);
    }

    const generic = genericById.get(item.generic_product_id);
    const variant = item.variant_id ? (variantById.get(item.variant_id) ?? null) : null;
    const price = variant ? (priceByVariant.get(variant.id) ?? null) : null;
    const fetchedAt = price?.fetched_at ?? null;
    const ageMs = fetchedAt ? now - Date.parse(fetchedAt) : null;

    group.items.push({
      id: item.id,
      // Falling back to the variant name keeps an item readable when its
      // generic has not synced yet; the id is the last resort, never blank.
      name: generic?.name ?? variant?.name ?? 'Onbekend product',
      category: generic?.category ?? null,
      detail: variant
        ? variantLabel({
            shortName: variant.short_name,
            name: variant.name,
            brand: variant.brand,
            sizeValue: variant.size_value,
            sizeUnit: variant.size_unit,
            genericName: generic?.name ?? null,
          })
        : null,
      sizeLabel: variant ? sizeLabelOf(variant) : null,
      // Only a chosen variant has a picture of its own. An "any store" item and
      // a manual entry have none by construction, so they borrow one from
      // another variant of the same product — the household's own picture of
      // the thing, which is what makes a tile recognisable against a shelf.
      // The lettered fallback is for products with no picture anywhere.
      imagePath: variant?.image_path ?? genericImages.get(item.generic_product_id) ?? null,
      quantity: item.quantity,
      note: item.note,
      suggestionReason: item.suggestion_reason,
      // Either the chosen variant or the generic being organic makes the row
      // organic — the same test AddScreen uses, so the list and the add screen
      // never disagree about the same product. An any-store item has no variant
      // and leans on the generic's flag.
      organic: !!(variant?.organic || generic?.organic),
      genericProductId: item.generic_product_id,
      variantId: item.variant_id,
      priceCents: price?.price_cents ?? null,
      priceFetchedAt: fetchedAt,
      // Never present an old price as current. An unknown age counts as stale.
      stale:
        price?.price_cents == null
          ? false
          : ageMs === null || !Number.isFinite(ageMs) || ageMs > input.staleAfterHours * 3600_000,
    });
  }

  for (const group of groups.values()) {
    group.items.sort((a, b) => {
      const pa = a.category
        ? (positionOf.get(`${group.storeId}|${a.category}`) ?? UNPOSITIONED)
        : UNPOSITIONED;
      const pb = b.category
        ? (positionOf.get(`${group.storeId}|${b.category}`) ?? UNPOSITIONED)
        : UNPOSITIONED;
      if (pa !== pb) return pa - pb;
      // Category name before product name, so a newly bought product already
      // sits with its aisle-mates rather than at the end of the group.
      const ca = (a.category ?? '￿').localeCompare(b.category ?? '￿');
      if (ca !== 0) return ca;
      return a.name.localeCompare(b.name);
    });
  }

  return [...groups.values()].sort((a, b) => {
    // Any always first.
    if (a.storeId === null) return -1;
    if (b.storeId === null) return 1;
    return a.label.localeCompare(b.label);
  });
}
